import React, { useRef, useState, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import 'react-color-palette/lib/css/styles.css';
import { useSelector } from 'react-redux';
import { IconButton } from '@material-ui/core';
import useContribution from 'pages/ContributionView/hooks/useContribution';
import Loader from 'components/UI/Loader';
import { getThemedColors, determineColorToUse, determineDarkThemedColorToUse } from 'services/contributions.service';
import { getApplicationFormByContribId } from 'services/applicationForm.services';
import { getStyledButton, getStyledSecondaryButton, pluckKeys } from 'utils/utils';
import styled, { css } from 'styled-components';
import Select from 'components/UI/Select';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import QuestionModal from './QuestionModal';
import { useStyles } from './useStyles';
import AddQuestion, { getContributionTextColorCode } from './AddQuestion';
import SvgHandler from './SvgHandler';
import { isUndefined } from 'lodash';

export const DEFAULT_SELECETED_CONTRIBUTION = 'no-contriution-selected';
export const DEFAULT_SELECETED_TEMPLATE = 'no-template-selected';

const selector = state => ({
  contributionsForTable: state?.upcomingCreated?.upcomingCreated?.contributionsForTable || [],
});
const StyledScrollBar = styled.div`
  ${({ isScrollVisible }) =>
    isScrollVisible
      ? css`
          padding-right: 15px;
          margin-right: -11px;
        `
      : css``}
  &::-webkit-scrollbar {
    padding-left: 15px;
  }
  scrollbar-color: ${({ colortoUse }) => colortoUse.AccentColorCode} #f5f5f5;
  &::-webkit-scrollbar-thumb {
    background-color: ${({ colortoUse }) => colortoUse.AccentColorCode};
    border-radius: 5px;
  }
  &::-webkit-scrollbar-track {
    background-color: #f5f5f5;
    border-radius: 5px;
  }
`;
const StyledSelectLabel = styled.span`
  color: #282b2b;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 30px;
`;

function ApplicationForm({
  questions,
  setQuestions,
  setSavedQuestions,
  selectedContribution,
  setSelectedContribution,
  template,
  fetchForm,
  setFetchForm,
  forms,
  setTemplate,
  contributionViewMode,
  questionsScrollIsVisible,
  setQuestionsScrollIsVisible,
  draftQuestion,
  setDraftQuestion,
  setTitle,
  setIsCancel,
}) {
  const [open, setOpen] = useState(false);
  const [inEditMode, setInEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [editableQuestion, setEditableQuestion] = useState({});
  const [templateQuestions, setTemplateQuestions] = useState();
  const { contributionsForTable } = useSelector(selector);
  const contribution = useContribution();
  const questionsContainerRef = useRef();
  const draftQuestionRef = useRef();
  const { themedColor, themedCardBackgroundColor } = getThemedColors(contribution);
  const [scrollIsVisible, setScrollIsVisible] = useState(false);
  const colorToUse = determineColorToUse(contribution?.activeContribution);
  const [editError, setEditError] = useState('');
  const styledProps = {
    backgroundColor: themedCardBackgroundColor,
    color: themedColor,
  };
  const colortoUse = determineColorToUse(contribution);
  const isDarkThemeEnabled = determineDarkThemedColorToUse(contribution);
  const classes = useStyles(styledProps);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    draftQuestion && setDraftQuestion(false);
    setOpen(false);
    setEditableQuestion({});
  };
  const handleEditQuestion = question => {
    if (Object.keys(editableQuestion).length === 0) {
      setEditError('');
      setEditableQuestion(question);
      setEditId(question.localId);
      setInEditMode(true);
    } else {
      setEditError('Please save your current draft questions before editing a new question.');
      if (questionsContainerRef?.current && draftQuestionRef?.current) {
        questionsContainerRef.current.scrollTop = draftQuestionRef.current.offsetTop - 60 - 20;
      }
    }
  };

  const handleDeleteQuestion = async questionToBeDeleted => {
    const updatedQuestions = questions
      .filter(question => {
        return question.localId !== questionToBeDeleted;
      })
      .map(filteredQuestions => {
        return filteredQuestions;
      });
    await setQuestions(updatedQuestions);
    setEditableQuestion({});
  };

  const generateLocalId = () => {
    return Math.floor(Math.random() * 10000) + 1;
  };

  const getTemplateQuesitons = () => {
    return template?.questions.map(question => {
      delete question.id;
      return { ...question, localId: generateLocalId() };
    });
  };

  const handleSelectContribution = async () => {
    setTitle(true);
    setIsCancel(true);
    setIsLoading(true);
    await getApplicationFormByContribId(selectedContribution)
      .then(data => {
        if (data && data.questions) {
          let updatedQuestions = data.questions?.map(question => {
            return {
              ...question,
              localId: generateLocalId(),
            };
          });
          setSavedQuestions(updatedQuestions);
          if (template) {
            const templateQuestionsTemp = getTemplateQuesitons();
            setTemplateQuestions(templateQuestionsTemp);
            updatedQuestions = [...updatedQuestions, ...templateQuestionsTemp];
          }
          setQuestions(updatedQuestions);
        }
      })
      .catch(() => {
        let updatedQuestions = [];
        setSavedQuestions(updatedQuestions);
        if (template) {
          const templateQuestionsTemp = getTemplateQuesitons();
          setTemplateQuestions(templateQuestionsTemp);
          updatedQuestions = templateQuestionsTemp;
        } else {
          setDraftQuestion(true);
        }
        setQuestions(updatedQuestions);
      });
    setIsLoading(false);
  };

  useEffect(() => {
    if (contribution?.applicationFormViewModel?.questions) {
      const updatedQuestions = contribution.applicationFormViewModel.questions?.map(question => {
        return {
          ...question,
          localId: Math.floor(Math.random() * 10000) + 1,
        };
      });
      setQuestions(updatedQuestions);
    }
    if (contribution?.id) {
      setSelectedContribution(contribution.id);
    }
    if (contributionViewMode) setDraftQuestion(true);
  }, []);

  useEffect(() => {
    if (!fetchForm) {
      handleClose();
    }
  }, [fetchForm]);

  useEffect(() => {
    if (templateQuestions) {
      const prevTemplateQuestions = pluckKeys(templateQuestions, 'localId');
      setQuestions(prevQuestions =>
        prevQuestions.filter(question => !prevTemplateQuestions.includes(question.localId)),
      );
      setSavedQuestions(prevQuestions =>
        prevQuestions.filter(question => !prevTemplateQuestions.includes(question.localId)),
      );
      setTemplateQuestions([]);
    }
    if (selectedContribution !== DEFAULT_SELECETED_CONTRIBUTION) {
      const templateQuestionsTemp = getTemplateQuesitons();
      setTemplateQuestions(templateQuestionsTemp);
      setQuestions(prevQuestions => [...prevQuestions, ...templateQuestionsTemp]);
    }
  }, [template]);

  useEffect(() => {
    if (!fetchForm) {
      return;
    }
    if (!contribution?.id) {
      setQuestions([]);
      if (selectedContribution !== DEFAULT_SELECETED_CONTRIBUTION) {
        if (fetchForm) {
          handleSelectContribution();
        }
      }
    }
  }, [selectedContribution, fetchForm]);

  useEffect(() => {
    setQuestionsScrollIsVisible(
      questionsContainerRef?.current?.scrollHeight > questionsContainerRef?.current?.clientHeight || false,
    );
    setScrollIsVisible(
      questionsContainerRef?.current?.scrollHeight > questionsContainerRef?.current?.clientHeight || false,
    );
  }, [
    questionsContainerRef?.current,
    questionsContainerRef?.current?.scrollHeight,
    questionsContainerRef?.current?.clientHeight,
  ]);

  const handleDragEnd = results => {
    const { source, destination } = results;
    if (!destination) return;

    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const reOrderedQuestions = [...questions];
    const destinationQuestion = reOrderedQuestions[destination.index];
    const [removedSection] = reOrderedQuestions.splice(source.index, 1);
    reOrderedQuestions.splice(destination.index, 0, removedSection);

    setQuestions(reOrderedQuestions);
  };

  const renderQuestion = (question, index, parentProvided) => {
    return (
      <>
        <Droppable droppableId={`question-${index}`}>
          {(provided, snapshot) => (
            <div {...provided.droppableProps} innerRef={provided.innerRef}>
              <div className="drag-container">
                <span {...parentProvided.dragHandleProps} style={{ paddingTop: '3px', marginLeft: '-5px' }}>
                  <DragIndicatorIcon className="drag-icon" />
                </span>
                <div className="question-container">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div
                      style={{
                        color: isDarkThemeEnabled ? 'white' : 'rgba(0, 0, 0, 0.87)',
                        fontFamily: 'Avenir',
                        fontSize: '1rem',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: '1.25rem',
                      }}
                    >
                      Question {index + 1}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton className="icon-button" onClick={() => handleEditQuestion(question)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                          <path
                            d="M15.4095 5.55499L3.71637 17.2769L3.00781 18.9922L4.72313 18.2836L16.445 6.59054L15.4095 5.55499Z"
                            stroke={isDarkThemeEnabled ? 'white' : '#215C73'}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M18.2586 2.70769L17.752 3.21386L18.7875 4.24941L19.2941 3.74281C19.427 3.60978 19.5017 3.42942 19.5017 3.24136C19.5017 3.0533 19.427 2.87294 19.2941 2.73992L19.2619 2.70769C19.196 2.64181 19.1178 2.58954 19.0317 2.55389C18.9456 2.51823 18.8534 2.49988 18.7602 2.49988C18.667 2.49988 18.5748 2.51823 18.4887 2.55389C18.4026 2.58954 18.3244 2.64181 18.2586 2.70769Z"
                            stroke={isDarkThemeEnabled ? 'white' : '#215C73'}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </IconButton>
                      <IconButton className="icon-button" onClick={() => handleDeleteQuestion(question.localId)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                          <path
                            d="M18.5625 4.12531H14.4375V3.09406C14.4375 2.63821 14.2564 2.20104 13.9341 1.87872C13.6118 1.55639 13.1746 1.37531 12.7188 1.37531H9.28125C8.82541 1.37531 8.38824 1.55639 8.06591 1.87872C7.74358 2.20104 7.5625 2.63821 7.5625 3.09406V4.12531H3.4375C3.25516 4.12531 3.0803 4.19774 2.95136 4.32667C2.82243 4.4556 2.75 4.63047 2.75 4.81281C2.75 4.99514 2.82243 5.17001 2.95136 5.29894C3.0803 5.42787 3.25516 5.50031 3.4375 5.50031H4.16797L4.98438 18.6023C5.04539 19.7561 5.92969 20.6253 7.04688 20.6253H14.9531C16.0759 20.6253 16.9426 19.7754 17.0156 18.6058L17.832 5.50031H18.5625C18.7448 5.50031 18.9197 5.42787 19.0486 5.29894C19.1776 5.17001 19.25 4.99514 19.25 4.81281C19.25 4.63047 19.1776 4.4556 19.0486 4.32667C18.9197 4.19774 18.7448 4.12531 18.5625 4.12531ZM8.27449 17.8753H8.25C8.07183 17.8754 7.90057 17.8064 7.77232 17.6827C7.64407 17.559 7.56885 17.3904 7.5625 17.2123L7.21875 7.5873C7.21225 7.40496 7.27846 7.22751 7.4028 7.09399C7.52713 6.96046 7.69942 6.8818 7.88176 6.87531C8.06409 6.86881 8.24154 6.93501 8.37507 7.05935C8.50859 7.18369 8.58726 7.35598 8.59375 7.53831L8.9375 17.1633C8.94077 17.2536 8.92622 17.3437 8.89466 17.4283C8.8631 17.513 8.81516 17.5906 8.75359 17.6567C8.69201 17.7229 8.618 17.7762 8.53579 17.8137C8.45359 17.8512 8.36479 17.8721 8.27449 17.8753ZM11.6875 17.1878C11.6875 17.3701 11.6151 17.545 11.4861 17.6739C11.3572 17.8029 11.1823 17.8753 11 17.8753C10.8177 17.8753 10.6428 17.8029 10.5139 17.6739C10.3849 17.545 10.3125 17.3701 10.3125 17.1878V7.56281C10.3125 7.38047 10.3849 7.2056 10.5139 7.07667C10.6428 6.94774 10.8177 6.87531 11 6.87531C11.1823 6.87531 11.3572 6.94774 11.4861 7.07667C11.6151 7.2056 11.6875 7.38047 11.6875 7.56281V17.1878ZM13.0625 4.12531H8.9375V3.09406C8.93698 3.04877 8.94552 3.00384 8.96261 2.9619C8.9797 2.91996 9.005 2.88186 9.03703 2.84983C9.06905 2.81781 9.10715 2.79251 9.14909 2.77542C9.19103 2.75832 9.23596 2.74979 9.28125 2.75031H12.7188C12.764 2.74979 12.809 2.75832 12.8509 2.77542C12.8928 2.79251 12.931 2.81781 12.963 2.84983C12.995 2.88186 13.0203 2.91996 13.0374 2.9619C13.0545 3.00384 13.063 3.04877 13.0625 3.09406V4.12531ZM14.4375 17.2123C14.4312 17.3904 14.3559 17.559 14.2277 17.6827C14.0994 17.8064 13.9282 17.8754 13.75 17.8753H13.7251C13.6348 17.8721 13.5461 17.8511 13.4639 17.8136C13.3818 17.7761 13.3078 17.7227 13.2463 17.6566C13.1847 17.5905 13.1368 17.5129 13.1053 17.4282C13.0738 17.3436 13.0592 17.2536 13.0625 17.1633L13.4063 7.53831C13.4095 7.44803 13.4304 7.35926 13.468 7.27708C13.5055 7.1949 13.5588 7.12092 13.6249 7.05935C13.691 6.99779 13.7686 6.94984 13.8533 6.91827C13.9379 6.88669 14.028 6.87209 14.1182 6.87531C14.2085 6.87852 14.2973 6.89949 14.3795 6.93701C14.4617 6.97453 14.5356 7.02787 14.5972 7.09399C14.6588 7.1601 14.7067 7.2377 14.7383 7.32234C14.7699 7.40698 14.7845 7.49701 14.7813 7.5873L14.4375 17.2123Z"
                            fill={isDarkThemeEnabled ? 'white' : '#215C73'}
                          />
                        </svg>
                      </IconButton>
                    </div>
                  </div>
                  <div className="row m-0 justify-content-between">
                    <Grid item xl={9} lg={9} md={9} sm={9} xs={12}>
                      <p className="mb-1 title">{question.name}</p>
                      {question.isOptional && <span className="optional"> (Optional)</span>}
                    </Grid>
                  </div>
                  {question.questionType === 'Text' && question.options.length < 1 ? (
                    <Grid item xl={12} lg={12} md={12} sm={11} xs={11}>
                      <div className="text-field big">Answer...</div>
                    </Grid>
                  ) : (
                    question.options.map(option => {
                      return (
                        <Grid key={Math.floor(Math.random() * 10000 + 1)} container>
                          {question.questionType === 'CheckBox' && (
                            <SvgHandler
                              className="create-application-select-box"
                              style={{ margin: 'auto 10px auto 0' }}
                              fill={themedColor}
                              height="24px"
                              width="24px"
                              viewBox="0 0 24 24"
                              pathD="M5.75,3 L18.25,3 C19.7687831,3 21,4.23121694 21,5.75 L21,18.25 C21,19.7687831 19.7687831,21 18.25,21 L5.75,21 C4.23121694,21 3,19.7687831 3,18.25 L3,5.75 C3,4.23121694 4.23121694,3 5.75,3 Z M5.75,4.5 C5.05964406,4.5 4.5,5.05964406 4.5,5.75 L4.5,18.25 C4.5,18.9403559 5.05964406,19.5 5.75,19.5 L18.25,19.5 C18.9403559,19.5 19.5,18.9403559 19.5,18.25 L19.5,5.75 C19.5,5.05964406 18.9403559,4.5 18.25,4.5 L5.75,4.5 Z"
                            />
                          )}
                          {question.questionType === 'Radio' && (
                            <SvgHandler
                              className="create-application-select-box"
                              style={{ margin: 'auto 10px auto 0' }}
                              fill={themedColor}
                              height="24px"
                              width="24px"
                              viewBox="0 0 24 24"
                              pathD="M12 21C10.22 21 8.47991 20.4722 6.99987 19.4832C5.51983 18.4943 4.36628 17.0887 3.68509 15.4442C3.0039 13.7996 2.82567 11.99 3.17294 10.2442C3.5202 8.49836 4.37737 6.89472 5.63604 5.63604C6.89472 4.37737 8.49836 3.5202 10.2442 3.17294C11.99 2.82567 13.7996 3.0039 15.4442 3.68509C17.0887 4.36628 18.4943 5.51983 19.4832 6.99987C20.4722 8.47991 21 10.22 21 12C21 14.387 20.0518 16.6761 18.364 18.364C16.6761 20.0518 14.387 21 12 21ZM12 4.5C10.5166 4.5 9.0666 4.93987 7.83323 5.76398C6.59986 6.58809 5.63856 7.75943 5.07091 9.12988C4.50325 10.5003 4.35473 12.0083 4.64411 13.4632C4.9335 14.918 5.64781 16.2544 6.6967 17.3033C7.7456 18.3522 9.08197 19.0665 10.5368 19.3559C11.9917 19.6453 13.4997 19.4968 14.8701 18.9291C16.2406 18.3614 17.4119 17.4001 18.236 16.1668C19.0601 14.9334 19.5 13.4834 19.5 12C19.5 10.0109 18.7098 8.10323 17.3033 6.6967C15.8968 5.29018 13.9891 4.5 12 4.5Z"
                            />
                          )}
                          <p
                            className="options"
                            style={{
                              margin: 'auto 0',
                              width: '90%',
                              fontSize: '16px',
                              fontStyle: 'normal',
                              fontWeight: 500,
                              lineHeight: '20px',
                            }}
                          >
                            {option}
                          </p>
                        </Grid>
                      );
                    })
                  )}
                </div>
              </div>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </>
    );
  };

  const contributionTextColorCode = getContributionTextColorCode(contribution, colorToUse);
  const StyledButton = getStyledButton(contribution, contributionTextColorCode);
  const StyledSecondaryButton = getStyledSecondaryButton();

  useEffect(() => {
    setScrollIsVisible(isUndefined(questionsScrollIsVisible) ? scrollIsVisible : questionsScrollIsVisible);
  }, [questionsScrollIsVisible]);

  return (
    <>
      <Grid item xl={12} lg={12} md={12} sm={12} xs={12} className="application-form">
        {fetchForm && template && (
          <>
            <StyledSelectLabel>Select Prior Template</StyledSelectLabel>
            <Select
              fullWidth
              className={`select mb-2 ${template === DEFAULT_SELECETED_TEMPLATE ? 'default' : 'null'}`}
              margin="dense"
              defaultIcon={true}
              value={template}
              onChange={({ target: { value } }) => {
                setTemplate(value);
              }}
              items={[{ title: 'Select Prior Template', value: DEFAULT_SELECETED_TEMPLATE, disabled: true }].concat(
                forms.map(form => {
                  return {
                    title: form.contributionName,
                    value: form,
                  };
                }),
              )}
            />
          </>
        )}
        {!contribution?.id && (
          <>
            {fetchForm && <StyledSelectLabel>Select Contribution</StyledSelectLabel>}
            <Select
              fullWidth
              className={`select ${selectedContribution === DEFAULT_SELECETED_CONTRIBUTION ? 'default' : 'null'}`}
              margin="dense"
              defaultIcon={true}
              value={selectedContribution}
              onChange={({ target }) => {
                setSelectedContribution(target.value);
              }}
              items={[{ title: 'Select Service', value: DEFAULT_SELECETED_CONTRIBUTION, disabled: true }]
                .concat(
                  contributionsForTable.map(contrib => {
                    if (
                      contrib.isAccountNeeded === true &&
                      contrib.status === 'Approved' &&
                      !(template && contrib.applicationFormViewModel)
                    ) {
                      return {
                        title: contrib.title,
                        value: contrib.id,
                      };
                    }
                  }),
                )
                .filter(value => value !== undefined)}
            />
          </>
        )}
        <StyledScrollBar
          ref={questionsContainerRef}
          className="content-container"
          colortoUse={colortoUse}
          isScrollVisible={scrollIsVisible}
        >
          {isLoading ? (
            <Loader />
          ) : (
            (contribution?.id || selectedContribution) && (
              <>
                {((questions && questions.length > 0) || open) && (
                  <div
                    style={{
                      fontSize: '20px',
                      fontWeight: '400',
                      marginTop: !contribution?.id ? '24px' : '0px',
                      marginBottom: '1rem',
                    }}
                  >
                    Questions
                  </div>
                )}
              </>
            )
          )}
          <Grid container>
            <Grid xl={12} lg={12} md={12} sm={12} xs={12} className="saved-question">
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="questions" type="question" className="test">
                  {provided => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {questions.map((question, index) => {
                        return editId === question.localId ? (
                          <>
                            <QuestionModal
                              open={open}
                              handleOpen={handleOpen}
                              handleClose={(type = '') => {
                                setEditId(null);
                                setEditableQuestion({});
                                setInEditMode(false);
                                // to remove in the future if in edit mode the option changes from delete to cancel
                                if (!open && type === 'delete') {
                                  handleDeleteQuestion(question.localId);
                                }
                              }}
                              questions={questions}
                              setQuestions={setQuestions}
                              options={options}
                              setOptions={setOptions}
                              editableQuestion={editableQuestion}
                              contributionViewMode={contributionViewMode}
                              editError={editError}
                              draftQuestionRef={draftQuestionRef}
                            />
                          </>
                        ) : (
                          <Fragment key={question.localId}>
                            <Draggable
                              draggableId={`question-${index}`}
                              key={`question-${index}`}
                              index={index}
                              isDragDisabled={open || editId}
                            >
                              {provided => (
                                <div {...provided.draggableProps} ref={provided.innerRef}>
                                  {renderQuestion(question, index, provided)}
                                </div>
                              )}
                            </Draggable>
                          </Fragment>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              {(open || draftQuestion) && (
                <>
                  <QuestionModal
                    open={open}
                    handleOpen={handleOpen}
                    handleClose={handleClose}
                    questions={questions}
                    setQuestions={setQuestions}
                    options={options}
                    setOptions={setOptions}
                    style={{
                      marginTop: '20px',
                    }}
                    contributionViewMode={contributionViewMode}
                    draftPlaceholder={draftQuestion}
                  />
                </>
              )}
            </Grid>
          </Grid>
          {fetchForm &&
            !draftQuestion &&
            !open &&
            !inEditMode &&
            (contribution?.id || (selectedContribution && selectedContribution !== DEFAULT_SELECETED_CONTRIBUTION)) && (
              <div style={{ paddingLeft: '8px' }} className="add-question">
                <AddQuestion handler={handleOpen} style={{ justifyContent: 'flex-end' }} />
              </div>
            )}
        </StyledScrollBar>
      </Grid>
    </>
  );
}
ApplicationForm.propTypes = {
  setQuestions: PropTypes.func,
  setSavedQuestions: PropTypes.func,
  questions: PropTypes.string,
  selectedContribution: PropTypes.string,
  setSelectedContribution: PropTypes.func,
  template: PropTypes.shape({
    questions: PropTypes.shape([
      {
        id: PropTypes.string,
      },
    ]),
  }).isRequired,
  fetchForm: PropTypes.bool,
  setFetchForm: PropTypes.func,
  forms: PropTypes.shape([]).isRequired,
  setTemplate: PropTypes.func.isRequired,
  contributionViewMode: PropTypes.bool.isRequired,
  questionsScrollIsVisible: PropTypes.bool,
  setQuestionsScrollIsVisible: PropTypes.func,
};
ApplicationForm.defaultProps = {
  setQuestions: () => {},
  setSavedQuestions: () => {},
  questions: [],
  selectedContribution: '',
  setSelectedContribution: () => {},
  fetchForm: false,
  setFetchForm: () => {},
  questionsScrollIsVisible: undefined,
  setQuestionsScrollIsVisible: () => {},
};
export default ApplicationForm;
